import {useStyles} from './TimeControl.styles';
import {Aggregation} from "../../@types/Query";
import moment from "moment";
import SelectControl from "../SelectControl/SelectControl";
import {useEffect} from "react";

export type TimeValue = {
    start: Date
    end: Date
}

export const defaultValue: TimeValue = {
    start: new Date(Date.now() - (1000 * 60 * 60 * 24 * 30)),
    end: new Date(),
}

type Props = {
    label?: string
    value: TimeValue,
    onChange: (value: TimeValue) => void
    aggregation?: Aggregation
    className?: string
}

const TimeControl = (
    {
        label = "Zeitraum",
        value,
        onChange,
        aggregation = "hour",
        className = ""
    }: Props
) => {
    const styles = useStyles();
    const start = sanitize[aggregation](value.start, "start");
    const end = sanitize[aggregation](value.end, "end");
    const startMoment = moment(start);
    const endMoment = moment(end);

    const handleChangeDateTime = (field: keyof TimeValue) => (dateFormat: string) => {
        onChange({
            ...value,
            [field]: sanitize[aggregation](new Date(Date.parse(dateFormat)), field),
        });
    }

    const handleChangeDate = (field: keyof TimeValue) => (dateFormat: string) => {
        const oldDate = value[field];
        const newDate = new Date(Date.parse(dateFormat));
        newDate.setHours(oldDate.getHours());
        newDate.setMinutes(oldDate.getMinutes());
        onChange({
            ...value,
            [field]: sanitize[aggregation](newDate, field),
        });
    }

    const handleChangeHour = (field: keyof TimeValue) => (hour: number) => {
        const date = new Date(value[field].getTime());
        date.setHours(hour);
        onChange({
            ...value,
            [field]: sanitize[aggregation](date, field),
        })
    }
    const handleChangeMonth = (field: keyof TimeValue) => (month: number) => {
        const date = new Date(value[field].getTime());
        date.setMonth(month-1);
        onChange({
            ...value,
            [field]: sanitize[aggregation](date, field),
        })
    }

    useEffect(()=>{
        // save sanitized values
        if(
            value.start.toISOString() != start.toISOString()
            ||
            value.end.toISOString() != end.toISOString()
        ){
            onChange({start, end});
        }

    }, [start.toISOString(), end.toISOString()])

    return (
        <div className={`${styles.component} ${className}`}>
            {label?.length > 0 ? <div className={styles.label}>{label}</div> : null}
            <div className={styles.options}>
                {
                    aggregation == "minutes_1" || aggregation == "minutes_5" ?
                        <>
                            <input
                                className={styles.input}
                                type="datetime-local"
                                value={startMoment.format("YYYY-MM-DDTHH:mm")}
                                step={aggregation == "minutes_1" ? 60 : 60 * 5}
                                onChange={(e) => handleChangeDateTime("start")(e.target.value)}
                            />
                            <span className={styles.divider}>bis</span>
                            <input
                                className={styles.input}
                                type="datetime-local"
                                value={endMoment.format("YYYY-MM-DDTHH:mm")}
                                step={aggregation == "minutes_1" ? 60 : 60 * 5}
                                onChange={(e) => handleChangeDateTime("end")(e.target.value)}
                            />
                        </>
                        : null
                }
                {
                    aggregation == "hour" ?
                        <>
                            <input
                                className={styles.input}
                                type="date"
                                value={startMoment.format("YYYY-MM-DD")}
                                onChange={(e) => handleChangeDate("start")(e.target.value)}
                            />
                            <SelectControl<number>
                                value={start.getHours()}
                                onChange={handleChangeHour("start")}
                                options={[...Array(24)].map((_, index) => ({value: index, label: `${index} Uhr`}))}
                            />
                            <span className={styles.divider}>bis</span>
                            <input
                                className={styles.input}
                                type="date"
                                value={endMoment.format("YYYY-MM-DD")}
                                onChange={(e) => handleChangeDate("end")(e.target.value)}
                            />
                            <SelectControl<number>
                                value={end.getHours()}
                                onChange={handleChangeHour("end")}
                                options={[...Array(25)].map((_, index) => ({value: index, label: `${index} Uhr`}))}
                            />
                        </>

                        :
                        null
                }
                {
                    aggregation == "day" ?
                        <>
                            <input
                                className={styles.input}
                                type="date"
                                value={startMoment.format("YYYY-MM-DD")}
                                onChange={(e) => handleChangeDate("start")(e.target.value)}
                            />
                            <span className={styles.divider}>bis</span>
                            <input
                                className={styles.input}
                                type="date"
                                value={endMoment.format("YYYY-MM-DD")}
                                onChange={(e) => handleChangeDate("end")(e.target.value)}
                            />
                        </>
                        :
                        null
                }
                {
                    aggregation == "month" ?
                        <>
                            <SelectControl<number>
                                value={start.getMonth()+1}
                                onChange={handleChangeMonth("start")}
                                options={[...Array(12)].map((_, index) => ({
                                    value: index+1,
                                    label: `${monthLabels[index]}`
                                }))}
                            />
                            <SelectControl<number>
                                value={start.getFullYear()}
                                onChange={(value) => handleChangeDateTime("start")(`${value}-${start.getMonth()+1}-01 00:00:00`)}
                                options={getAvailableYears().map(year => ({value: year, label: `${year}`}))}
                            />
                            <span className={styles.divider}>bis</span>
                            <SelectControl<number>
                                value={end.getMonth()+1}
                                onChange={handleChangeMonth("end")}
                                options={[...Array(12)].map((_, index) => ({
                                    value: index+1,
                                    label: `${monthLabels[index]}`
                                }))}
                            />
                            <SelectControl<number>
                                value={end.getFullYear()}
                                onChange={(value) => handleChangeDateTime("end")(`${value}-${end.getMonth()+1}-31 23:59:59`)}
                                options={getAvailableYears().map(year => ({value: year, label: `${year}`}))}
                            />
                        </>
                        :
                        null
                }
            </div>
        </div>
    )
}

export default TimeControl;

const sanitize: {
    [key in Aggregation]: (date: Date, field: keyof TimeValue) => Date
} = {
    minutes_1(date, field): Date {
        const d = new Date(date.getTime());
        d.setMilliseconds(field == "start" ? 0 : 999);
        d.setSeconds(field == "start" ? 0: 59);
        return d;
    },
    minutes_5: (date, field): Date => {
        const d = sanitize.minutes_1(date, field);
        const rest = d.getMinutes() % 5;
        d.setMinutes(rest < 3 ? d.getMinutes() - rest : d.getMinutes() + (5 - rest));
        return d;
    },
    hour(date: Date, field): Date {
        const d = sanitize.minutes_1(date, "start");
        d.setMinutes(0 );
        return d;
    },
    day(date: Date, field): Date {
        const d = sanitize.hour(date, field);
        if(field == "end"){
            d.setMilliseconds(999);
            d.setSeconds(59);
            d.setMinutes(59);
        }
        d.setHours(field == "start" ? 0 : 23);
        return d;
    },
    month(date: Date, field): Date {
        const d = sanitize.day(date, field);
        d.setMilliseconds(50);
        if(field == "start"){
            d.setDate(1);
        } else {
            const lastDay = new Date(d.getFullYear(), d.getMonth()+1, 0);
            d.setDate(lastDay.getDate());
        }
        return d;
    },
    year(date: Date, field): Date {
        const d = sanitize.month(date, field);
        d.setMonth(field == "start" ? 0 : 11);
        return d;
    }
}

const getAvailableYears = () => {
    const now = new Date();
    return [...Array(now.getFullYear() - 2010)].map((_, index) => 2010 + index + 1);
}

const monthLabels = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
]